import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Mail, ShieldAlert } from "lucide-react";

/**
 * Email Send Guard (2026-02) — the shared "This will email N people" confirm dialog for every
 * USER-triggered email this app sends to anyone outside @njmpro.com or to more than one
 * recipient. `preview` is exactly the `preview` object a guarded endpoint's first (no-token)
 * call returns: { subject, body_excerpt, count, recipients: [{name,email,role,blocked,
 * blocked_label}], needs_typed_count }. `onConfirm(typedCount)` fires the real, token-bearing
 * second call; `onCancel` just closes the dialog — nothing was ever written or sent.
 */
export function EmailConfirmModal({ open, preview, busy, onConfirm, onCancel }) {
  const [typedCount, setTypedCount] = useState("");
  if (!preview) return null;
  const needsTypedCount = !!preview.needs_typed_count;
  const typedOk = !needsTypedCount || Number(typedCount) === preview.count;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="sm:max-w-lg" data-testid="email-confirm-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            This will email {preview.count} {preview.count === 1 ? "person" : "people"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Subject:</span> {preview.subject}
          </div>
          {preview.body_excerpt && (
            <div className="text-sm text-muted-foreground line-clamp-2">{preview.body_excerpt}</div>
          )}
          <div className="max-h-48 overflow-y-auto rounded-md border border-hairline divide-y" data-testid="email-confirm-recipient-list">
            {preview.recipients.map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`email-confirm-recipient-${i}`}>
                <div className="min-w-0">
                  <div className="truncate font-medium">{r.name || r.email}</div>
                  <div className="truncate text-xs text-muted-foreground">{r.email} · {r.role}</div>
                </div>
                {r.blocked && (
                  <span className="flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800" data-testid={`email-confirm-blocked-${i}`}>
                    <ShieldAlert className="h-3 w-3" /> {r.blocked_label || "blocked"}
                  </span>
                )}
              </div>
            ))}
          </div>
          {needsTypedCount && (
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="email-confirm-typed-count">
                Type {preview.count} to confirm you want to email all {preview.count} people
              </label>
              <Input
                id="email-confirm-typed-count"
                data-testid="email-confirm-typed-count-input"
                value={typedCount}
                onChange={(e) => setTypedCount(e.target.value)}
                placeholder={String(preview.count)}
                inputMode="numeric"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" data-testid="email-confirm-cancel-button" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            data-testid="email-confirm-send-button"
            onClick={() => onConfirm(needsTypedCount ? Number(typedCount) : undefined)}
            disabled={busy || !typedOk}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
